import { Link, useLocation } from '@tanstack/react-router'
import { Card } from '../components/primitives/Card'
import { Icon } from '../components/primitives/Icon'
import { Button } from '../components/primitives/Button'

/**
 * 404 catch-all page rendered when a URL doesn't match any route.
 * Warm, calm, paperlike — consistent with the brand voice.
 */
export function NotFoundPage(): JSX.Element {
  const location = useLocation()

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px 16px',
        background: 'var(--paper-sunken)',
      }}
    >
      <Card
        padding={48}
        data-testid="not-found-card"
        style={{ maxWidth: 480, width: '100%' }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 16,
          }}
        >
          <div aria-hidden="true" style={{ color: 'var(--ink-3)', lineHeight: 1 }}>
            <Icon name="explore_off" size={48} />
          </div>

          <h1
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 24,
              fontWeight: 400,
              margin: 0,
              color: 'var(--ink)',
              lineHeight: 1.3,
            }}
          >
            This page took some leave.
          </h1>

          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              color: 'var(--ink-2)',
              maxWidth: 360,
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            The URL you followed doesn't exist or has moved.
          </p>

          {location.pathname ? (
            <p
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--ink-3)',
                background: 'var(--paper-sunken)',
                border: '1px solid var(--line)',
                borderRadius: 4,
                padding: '4px 8px',
                margin: 0,
              }}
              data-testid="not-found-path"
            >
              Tried: {location.pathname}
            </p>
          ) : null}

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              justifyContent: 'center',
              marginTop: 8,
            }}
          >
            <Link to="/" data-testid="not-found-home-link">
              <Button variant="primary" size="md">
                Back to home
              </Button>
            </Link>

            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                window.location.href = `mailto:support@lms.local?subject=Broken+link&body=I+followed+a+link+to+${encodeURIComponent(location.pathname)}+and+got+a+404.`
              }}
              data-testid="not-found-report-link"
            >
              Report a broken link
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
